import { useState } from 'react';
import { Button } from '../../../components/Button';
import { TextArea, TextInput } from '../../../components/Field';
import { Modal } from '../../../overlay/Modal';
import { useOverlay } from '../../../overlay/context';
import { toast } from '../../../components/Toast';
import { useAuthStore } from '../../../app/authStore';
import { getSession } from '../../../drone/store';
import type { LinkSnapshot } from '../../../drone/session';
import {
  COMMAND_GROUP_LABELS,
  DRONE_COMMANDS,
  buildCommandPayload,
  commandAvailability,
  commandButtonLabel,
  commandConfirmTitle,
  commandTone,
} from '@shared/commands';
import type { CommandGroup, DroneCommandMeta, DroneCommandType } from '@shared/commands';
import { MISSION_STATE_LABELS } from '@shared/format';
import type { DroneTelemetry, Waypoint } from '@shared/types';

interface CommandBarProps {
  droneId: string;
  telemetry: DroneTelemetry | null;
  link: LinkSnapshot | null;
  staleAfterMs: number;
  /** 无人机上报的能力清单；为 null 时按平台全量指令展示 */
  supportedCommands: readonly string[] | null;
  className?: string;
}

const GROUP_ORDER: readonly CommandGroup[] = ['flight', 'mission', 'maintenance'];

/**
 * 单机管理的指令面板。
 *
 * 全部指令都来自 shared 的指令目录：按钮可用性用 `commandAvailability` 判定，
 * 参数用 `buildCommandPayload` 校验，因此前端不会出现「界面上能用、后端拒绝」的指令。
 * 危险指令走统一样式的确认浮层（无蒙版、从按钮飞出），不会调用浏览器原生弹窗。
 */
export function CommandBar({
  droneId,
  telemetry,
  link,
  staleAfterMs,
  supportedCommands,
  className = '',
}: CommandBarProps) {
  const overlay = useOverlay();
  const permissions = useAuthStore((state) => state.permissions);

  const [busy, setBusy] = useState<DroneCommandType | null>(null);
  const [paramType, setParamType] = useState<DroneCommandType | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [paramErrors, setParamErrors] = useState<Record<string, string>>({});
  const [missionOpen, setMissionOpen] = useState(false);

  const frameAgeMs = telemetry ? Date.now() - telemetry.receivedAt : null;
  const offline = frameAgeMs === null || frameAgeMs > staleAfterMs;
  const missionState = telemetry?.flight.missionState ?? 'idle';
  const missionSummary = telemetry
    ? `任务状态：${MISSION_STATE_LABELS[missionState]} · 航点 ${telemetry.flight.missionCurrentWaypoint}/${telemetry.flight.missionTotalWaypoints}`
    : '尚未收到飞控数据';

  const availabilityCtx = {
    armed: Boolean(telemetry?.flight.armed),
    inAir: Boolean(telemetry?.flight.inAir),
    missionState,
    missionTotalWaypoints: telemetry?.flight.missionTotalWaypoints ?? 0,
  };

  async function dispatch(type: DroneCommandType, values: Record<string, unknown>): Promise<boolean> {
    const session = getSession(droneId);
    if (!session) {
      toast.critical('链路会话尚未建立', '请等待链路连接成功后再下发指令。');
      return false;
    }
    const built = buildCommandPayload(type, values);
    if (!built.ok) {
      toast.warning('指令参数有误', built.errors.map((error) => error.message).join('；'));
      return false;
    }

    setBusy(type);
    try {
      if (type === 'mission-upload') {
        // 航线上传走无人机服务器上独立的 `/mission` 资源，与航线编辑器共用同一条通路
        await session.uploadMission(toWaypoints(built.payload.waypoints));
        toast.ok('航线已上传', `共 ${Array.isArray(built.payload.waypoints) ? built.payload.waypoints.length : 0} 个航点`);
      } else {
        await session.sendCommand(type, built.payload);
        if (type === 'emergency-stop') {
          toast.critical('紧急停止已下发', '请立即确认飞行器状态并上报。');
        } else {
          toast.ok(`${DRONE_COMMANDS[type].label}已下发`);
        }
      }
      return true;
    } catch (error) {
      toast.critical(
        `${DRONE_COMMANDS[type].label}下发失败`,
        error instanceof Error ? error.message : String(error),
      );
      return false;
    } finally {
      setBusy(null);
    }
  }

  function openParamForm(type: DroneCommandType) {
    const initial: Record<string, string> = {};
    for (const spec of DRONE_COMMANDS[type].params) {
      initial[spec.name] = spec.defaultValue === undefined ? '' : String(spec.defaultValue);
    }
    setParamValues(initial);
    setParamErrors({});
    setParamType(type);
  }

  async function handleClick(type: DroneCommandType, trigger: HTMLElement | null) {
    if (type === 'mission-upload') {
      setMissionOpen(true);
      return;
    }

    const meta = DRONE_COMMANDS[type];
    if (meta.params.length > 0) {
      openParamForm(type);
      return;
    }

    const availability = commandAvailability(type, availabilityCtx);
    if (!availability.enabled) {
      toast.caution(availability.reason ?? '当前状态下不可执行该指令');
      return;
    }

    if (meta.destructive) {
      const confirmed = await overlay.confirm({
        placementKey: `command:${type}`,
        trigger,
        title: commandConfirmTitle(meta),
        description: (
          <div className="flex flex-col gap-1.5">
            <span className="font-semibold text-fg">{meta.description}</span>
            {meta.preflight?.length ? (
              <PreflightList meta={meta} />
            ) : (
              <span>该操作会立即改变飞行器状态，下发后无法撤回。</span>
            )}
          </div>
        ),
        confirmText: `确认${meta.label}`,
        cancelText: '取消',
        tone: commandTone(meta),
      });
      if (!confirmed) return;
    }

    await dispatch(type, {});
  }

  async function submitParamCommand(type: DroneCommandType, trigger: HTMLElement | null) {
    const meta = DRONE_COMMANDS[type];
    const built = buildCommandPayload(type, paramValues);
    if (!built.ok) {
      const next: Record<string, string> = {};
      for (const error of built.errors) next[error.field] = error.message;
      setParamErrors(next);
      return;
    }
    setParamErrors({});

    if (meta.destructive) {
      const confirmed = await overlay.confirm({
        placementKey: `command:${type}:submit`,
        trigger,
        title: commandConfirmTitle(meta),
        description: meta.description,
        confirmText: `确认${meta.label}`,
        cancelText: '取消',
        tone: commandTone(meta),
      });
      if (!confirmed) return;
    }

    if (await dispatch(type, paramValues)) setParamType(null);
  }

  function renderCommand(type: DroneCommandType) {
    const meta = DRONE_COMMANDS[type];
    const tone = commandTone(meta);
    const allowed = permissions.includes(meta.permission);
    const supported = !supportedCommands || supportedCommands.includes(type);
    const availability = commandAvailability(type, availabilityCtx);
    const disabledReason = !allowed
      ? '当前账号没有该指令权限'
      : !supported
        ? '该机型上报的能力清单中不包含此指令'
        : offline
          ? '遥测数据已中断，禁止下发指令'
          : availability.reason;

    return (
      <Button
        key={type}
        // 蓝色强调（accent）用于危险但高频的指令：起飞、降落
        variant={
          type === 'emergency-stop' ? 'dangerSolid' : tone === 'accent' ? 'primary' : tone === 'danger' ? 'danger' : 'default'
        }
        disabled={busy !== null || Boolean(disabledReason)}
        title={disabledReason ?? meta.description}
        onClick={(event) => void handleClick(type, event.currentTarget)}
        className={type === 'emergency-stop' ? 'px-5 font-bold' : undefined}
      >
        {busy === type ? '下发中…' : commandButtonLabel(meta)}
      </Button>
    );
  }

  const paramMeta = paramType ? DRONE_COMMANDS[paramType] : null;

  return (
    <div className={['flex flex-col gap-3', className].join(' ')}>
      {offline ? (
        <p className="rounded border-2 border-critical bg-critical-soft px-3 py-2 text-xs font-bold text-fg">
          遥测数据已中断（链路状态：{link ? LINK_LABEL[link.phase] : '未连接'}），全部指令已锁定。
          失联状态下任何飞行操作都可能造成不可预期的后果。
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 rounded-lg border-2 border-critical bg-critical-soft px-3 py-2">
        <div className="flex min-w-0 flex-col">
          <span className="text-xs font-bold tracking-wide text-fg">紧急操作</span>
          <span className="text-[11px] leading-tight text-fg-muted">
            仅在人员或财产面临即刻危险时使用，将立即切断动力
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2">{renderCommand('emergency-stop')}</div>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
        {GROUP_ORDER.map((group) => (
          <div
            key={group}
            className="flex flex-col gap-2 rounded-lg border border-line bg-surface-1 p-2.5"
          >
            <span className="text-xs font-bold tracking-wide text-fg-muted">
              {COMMAND_GROUP_LABELS[group]}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(DRONE_COMMANDS) as DroneCommandType[])
                .filter((type) => DRONE_COMMANDS[type].group === group && type !== 'emergency-stop')
                .map(renderCommand)}
            </div>
          </div>
        ))}
      </div>

      <MissionPanel
        droneId={droneId}
        disabled={busy !== null || offline}
        open={missionOpen}
        onOpenChange={setMissionOpen}
        summary={missionSummary}
      />

      <Modal
        open={paramMeta !== null}
        onClose={() => setParamType(null)}
        title={paramMeta ? commandButtonLabel(paramMeta) : ''}
        description={paramMeta?.description}
        size="md"
        tone={paramMeta ? commandTone(paramMeta) : 'default'}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setParamType(null)}>取消</Button>
            <Button
              variant={paramMeta ? (commandTone(paramMeta) === 'danger' ? 'dangerSolid' : 'primary') : 'primary'}
              loading={busy !== null}
              onClick={(event) => {
                if (paramType) void submitParamCommand(paramType, event.currentTarget);
              }}
            >
              {paramMeta ? `确认${paramMeta.label}` : '确认'}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          {paramMeta?.params.map((spec) => (
            <TextInput
              key={spec.name}
              label={spec.unit ? `${spec.label}（${spec.unit}）` : spec.label}
              required={spec.required}
              error={paramErrors[spec.name]}
              hint={spec.help}
              type={spec.type === 'number' ? 'number' : 'text'}
              value={paramValues[spec.name] ?? ''}
              placeholder={spec.placeholder}
              onChange={(event) =>
                setParamValues((current) => ({ ...current, [spec.name]: event.target.value }))
              }
            />
          ))}
          {paramMeta ? <CommandWarning meta={paramMeta} /> : null}
        </div>
      </Modal>
    </div>
  );
}

/**
 * 危险指令的下发前提示。
 *
 * 起飞/降落这类「危险但高频」的指令带 `preflight` 条件检查清单，
 * 用蓝色强调（`accent`）表达「请逐项确认」，而不是红色告警；
 * 其余危险指令仍使用红色拦截式提示。
 */
function CommandWarning({ meta }: { meta: DroneCommandMeta }) {
  if (meta.preflight?.length) return <PreflightList meta={meta} />;
  if (!meta.destructive) return null;
  return (
    <p className="rounded border border-critical bg-critical-soft px-3 py-2 text-xs font-semibold text-fg">
      该指令为危险操作，下发后将立即执行且无法撤回。
    </p>
  );
}

function PreflightList({ meta }: { meta: DroneCommandMeta }) {
  if (!meta.preflight?.length) return null;
  return (
    <div className="flex flex-col gap-1.5 rounded border border-accent bg-accent-soft px-3 py-2">
      <span className="text-xs font-bold text-accent">{commandButtonLabel(meta)}</span>
      <ul className="flex flex-col gap-1">
        {meta.preflight.map((item) => (
          <li key={item} className="flex gap-1.5 text-xs leading-tight text-fg">
            <span aria-hidden className="text-accent">
              •
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <span className="text-[11px] leading-tight text-fg-muted">
        确认后立即执行，无法撤回。
      </span>
    </div>
  );
}

interface MissionPanelProps {
  droneId: string;
  disabled: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: string;
}

/**
 * 航线编辑面板：读取 / 编辑 / 上传航点。
 *
 * 走无人机服务器上独立的 `/mission` 资源，文本解析复用 shared 的
 * `parseWaypointText`（经由 `buildCommandPayload`），保证与后端校验规则一致。
 */
function MissionPanel({ droneId, disabled, open, onOpenChange, summary }: MissionPanelProps) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  async function loadFromDrone() {
    const session = getSession(droneId);
    if (!session) {
      toast.critical('链路会话尚未建立');
      return;
    }
    setBusy(true);
    try {
      const mission = await session.fetchMission();
      setText(
        mission.waypoints
          .map((waypoint) => `${waypoint.lat},${waypoint.lng},${waypoint.altM},${waypoint.speedMs}`)
          .join('\n'),
      );
      setError(undefined);
      toast.ok('已读取机上航线', `共 ${mission.waypoints.length} 个航点`);
    } catch (cause) {
      toast.critical('读取航线失败', cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function upload() {
    const session = getSession(droneId);
    if (!session) {
      toast.critical('链路会话尚未建立');
      return;
    }
    const built = buildCommandPayload('mission-upload', { waypoints: text });
    if (!built.ok) {
      setError(built.errors[0]?.message ?? '航点数据不合法');
      return;
    }
    setError(undefined);
    setBusy(true);
    try {
      const waypoints = toWaypoints(built.payload.waypoints);
      await session.uploadMission(waypoints);
      toast.ok('航线已上传', `共 ${waypoints.length} 个航点`);
      onOpenChange(false);
    } catch (cause) {
      toast.critical('上传航线失败', cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line bg-surface-1 p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold tracking-wide text-fg-muted">航线编辑</span>
        <span className="tabular text-[11px] text-fg-subtle">{summary}</span>
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            onClick={() => onOpenChange(!open)}
            disabled={disabled && !open}
            aria-expanded={open}
          >
            {open ? '收起航线编辑器' : '打开航线编辑器'}
          </Button>
        </div>
      </div>

      {open ? (
        <div className="flex flex-col gap-2">
          <TextArea
            label="航点列表"
            hint="每行一个航点：纬度,经度,相对高度(m),速度(m/s)。省略速度时默认 5 m/s。"
            error={error}
            rows={8}
            spellCheck={false}
            className="font-mono text-xs"
            value={text}
            placeholder={'39.907500,116.391300,30,5\n39.908100,116.392700,45,5'}
            onChange={(event) => setText(event.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" loading={busy} onClick={() => void loadFromDrone()}>
              读取机上航线
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={busy}
              disabled={text.trim() === ''}
              onClick={() => void upload()}
            >
              上传航线到飞行器
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setText('')}>
              清空
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** 指令目录里只产出 {lat,lng,altM,speedMs}，补齐航点结构要求的 holdSec。 */
function toWaypoints(value: unknown): Waypoint[] {
  if (!Array.isArray(value)) return [];
  return (value as Omit<Waypoint, 'holdSec'>[]).map((waypoint) => ({
    lat: waypoint.lat,
    lng: waypoint.lng,
    altM: waypoint.altM,
    speedMs: waypoint.speedMs,
    holdSec: 0,
  }));
}

const LINK_LABEL: Record<LinkSnapshot['phase'], string> = {
  idle: '未连接',
  connecting: '连接中',
  streaming: '实时推送',
  polling: '轮询中',
  retrying: '重连中',
  blocked: '已阻断',
  stopped: '已停止',
};
